import { useEffect } from "react";

// Canonical site origin for SEO/JSON-LD. Defaults to the production domain so
// search engines always index the right URL even when the app is served from
// a preview/staging host. Override with REACT_APP_SITE_URL when needed.
export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://wasz.nl").replace(/\/$/, "");
export const siteUrl = (path = "/") => `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;

const ensureMeta = (selector, attr, key, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const ensureLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const ensureJsonLd = (id, data) => {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const removeJsonLd = (id) => {
  const el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (el) el.remove();
};

/**
 * useSEO - per-page SEO meta tags + structured data
 * @param {object} opts
 *  - title, description, canonical, image, lang
 *  - jsonLd: array of { id, data } objects
 */
export const useSEO = ({
  title,
  description,
  canonical,
  image,
  lang = "nl",
  robots = "index,follow,max-image-preview:large,max-snippet:-1",
  jsonLd = [],
} = {}) => {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      ensureMeta('meta[name="description"]', "name", "description", description);
      ensureMeta('meta[property="og:description"]', "property", "og:description", description);
      ensureMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    }
    if (title) {
      ensureMeta('meta[property="og:title"]', "property", "og:title", title);
      ensureMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    }
    if (image) {
      // Facebook requires absolute URLs for og:image; convert relative paths defensively.
      const absImage = /^https?:\/\//i.test(image) ? image : siteUrl(image);
      ensureMeta('meta[property="og:image"]', "property", "og:image", absImage);
      ensureMeta('meta[property="og:image:secure_url"]', "property", "og:image:secure_url", absImage);
      ensureMeta('meta[name="twitter:image"]', "name", "twitter:image", absImage);
    }
    if (canonical) {
      ensureLink("canonical", canonical);
      ensureMeta('meta[property="og:url"]', "property", "og:url", canonical);
    }
    ensureMeta('meta[property="og:locale"]', "property", "og:locale", lang === "nl" ? "nl_NL" : "en_US");
    // Always explicitly write robots meta. This prevents stale noindex
    // values leaking between SPA route changes (e.g. from a NotFound mount).
    ensureMeta('meta[name="robots"]', "name", "robots", robots);

    const ids = [];
    jsonLd.forEach((entry) => {
      if (entry && entry.id && entry.data) {
        ensureJsonLd(entry.id, entry.data);
        ids.push(entry.id);
      }
    });

    return () => {
      ids.forEach(removeJsonLd);
    };
  }, [title, description, canonical, image, lang, robots, JSON.stringify(jsonLd)]);
};

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: item.url,
  })),
});

export const faqPageJsonLd = (faqs, lang = "nl") => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: lang === "nl" ? f.q_nl : f.q_en,
    acceptedAnswer: {
      "@type": "Answer",
      text: lang === "nl" ? f.a_nl : f.a_en,
    },
  })),
});

// Aggregate rating object based on genuine Google reviews (14 reviews · 5.0).
// IMPORTANT: Google only supports aggregateRating on specific schema types
// (LocalBusiness, Product, Recipe, Movie, Book, Course, Event, HowTo,
// SoftwareApplication, MusicRecording, etc.). It is NOT supported on
// generic `Service` — attaching it there causes GSC to report
// "Invalid object type for field parent_node" and can suppress rich results.
// We keep this object exported only for use on LocalBusiness markup.
export const aggregateRatingObj = {
  "@type": "AggregateRating",
  ratingValue: "5.0",
  bestRating: "5",
  reviewCount: "14",
};

export const serviceJsonLd = ({ name, description, areaServed = "Nederland", url }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  provider: {
    "@type": "Organization",
    name: "Wasz.nl",
    url: `${SITE_URL}/`,
  },
  areaServed,
  url,
});

export const localBusinessJsonLd = ({ city, region, withRating = true }) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: `Wasz.nl Truckwash ${city}`,
  image: `${SITE_URL}/images/project19.jpg`,
  url: `${SITE_URL}/truckwash-${city.toLowerCase()}`,
  telephone: "+31-36-525-5021",
  address: {
    "@type": "PostalAddress",
    addressLocality: city,
    addressRegion: region,
    addressCountry: "NL",
  },
  areaServed: city,
  priceRange: "€€",
  sameAs: ["https://www.facebook.com/Wasz.nl"],
  ...(withRating ? { aggregateRating: aggregateRatingObj } : {}),
});

export const articleJsonLd = (post, locale = "nl") => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: locale === "nl" ? post.title_nl : post.title_en,
  description: locale === "nl" ? post.excerpt_nl : post.excerpt_en,
  image: post.image && post.image.startsWith("http") ? post.image : `${SITE_URL}${post.image || ""}`,
  datePublished: post.date,
  author: { "@type": "Organization", name: "Wasz.nl" },
  publisher: {
    "@type": "Organization",
    name: "Wasz.nl",
    logo: { "@type": "ImageObject", url: `${SITE_URL}/wasz-logo.png` },
  },
});

export const videoObjectJsonLd = ({
  name,
  description,
  thumbnailUrl,
  uploadDate,
  contentUrl,
  embedUrl,
  duration,
  publisher = "Wasz.nl",
}) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name,
  description,
  thumbnailUrl: Array.isArray(thumbnailUrl) ? thumbnailUrl : [thumbnailUrl],
  uploadDate,
  ...(duration ? { duration } : {}),
  ...(contentUrl ? { contentUrl } : {}),
  ...(embedUrl ? { embedUrl } : {}),
  publisher: {
    "@type": "Organization",
    name: publisher,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/wasz-logo.png` },
  },
});
